import { EventEmitter, Injectable } from '@angular/core';
import { AbcCore, CompareHelper } from '@barthauer/abc/core';
import {
  DataService,
  ObjectSpaceService,
  ObjectTypeInfo,
  ObjectTypeInfoColumnType,
  ReadListDataSourceModel,
} from '@barthauer/abc/core/data';
import { BceService } from '@barthauer/abc/management/configuration-explorer';
import { AbcExpressionsEditorService } from '@barthauer/abc/queries/opath/expressions-editor';
import { ListDataSource } from 'libs/abc/arena/src/lib/core/models/data.model';
import { EditorService } from 'libs/abc/queries/opath/expressions-editor/src/lib/services/editor.service';
import {
  EditorInstanceSettings,
  ExpressionEditorResult,
  ExpressionEditorSettings,
  ValidationType,
} from 'libs/abc/ui-components/src/lib/core/src/abc-grid/abc-grid';
import { ParameterDefinition } from '../../../core/src/lib/models/parameter-definition.model';

@Injectable({
  providedIn: 'root',
})
export class DefinitionsEditorService {
  private _parameterDefinitionsQueue: Map<number, ParameterDefinition[]> = new Map();
  private _objectTypesInfo: ObjectTypeInfo[] = [];
  private _pickListByNameDataSource: ListDataSource;
  private _pickListByColumnDataSource: { Name: string; DisplayText: string }[];

  public onNewParameterDefinitions: EventEmitter<ParameterDefinition[]> = new EventEmitter<ParameterDefinition[]>();

  public set objectTypesInfo(value: ObjectTypeInfo[]) {
    this._objectTypesInfo = value;
  }
  public get objectTypesInfo(): ObjectTypeInfo[] {
    return this._objectTypesInfo;
  }

  public set pickListByNameDataSource(value: ListDataSource) {
    this._pickListByNameDataSource = value;
  }
  public get pickListByNameDataSource(): ListDataSource {
    return this._pickListByNameDataSource;
  }

  public set pickListByColumnDataSource(value: { Name: string; DisplayText: string }[]) {
    this._pickListByColumnDataSource = value;
  }
  public get pickListByColumnDataSource(): { Name: string; DisplayText: string }[] {
    return this._pickListByColumnDataSource;
  }

  constructor(
    private objectSpaceService: ObjectSpaceService,
    private abcCore: AbcCore,
    private editorService: EditorService,
    private expressionsEditorService: AbcExpressionsEditorService,
    private coreDataService: DataService,
    private bceService: BceService
  ) {}

  public setParameterDefinitionsQueue(key: number, value: ParameterDefinition[]) {
    this._parameterDefinitionsQueue.set(key, value);
  }

  public getParameterDefinitionsQueue(key: number): ParameterDefinition[] {
    return this._parameterDefinitionsQueue.get(key);
  }

  public removeParameterDefinitionsQueueElement(key: number): void {
    this._parameterDefinitionsQueue.delete(key);
  }

  public getObjectTypesInfo(): ObjectTypeInfo[] {
    if (this.objectTypesInfo.length) {
      return this.objectTypesInfo;
    } else {
      return this.objectSpaceService.getAllObjectTypesInfo();
    }
  }

  public async getPicklistByNameDataSource(): Promise<ListDataSource> {
    const query: ReadListDataSourceModel = new ReadListDataSourceModel();
    if (!this.pickListByNameDataSource) {
      const data: ListDataSource = await this.coreDataService.executeReadPicklistsQuery(query).toPromise();
      if (data?.Data) {
        data.Data = data.Data.sort((a, b) => CompareHelper.compare(a?.Designation, b?.Designation));
      }
      this.pickListByNameDataSource = data;
    }
    return this.pickListByNameDataSource;
  }

  public async getPicklistByColumnDataSource(): Promise<{ Name: string; DisplayText: string }[]> {
    return new Promise((resolve) => {
      if (!this.pickListByColumnDataSource) {
        this.pickListByColumnDataSource = [];
        for (const objectTypeInfo of this.getObjectTypesInfo()) {
          for (const column of objectTypeInfo.Columns) {
            if (column.ColumnType === ObjectTypeInfoColumnType.Picklist) {
              this.pickListByColumnDataSource.push({
                Name: objectTypeInfo.Name + '.' + column.Name,
                DisplayText: objectTypeInfo.DisplayText + '.' + column.DisplayText,
              });
            }
          }
        }
      }
      if (this.pickListByColumnDataSource) resolve(this.pickListByColumnDataSource);
    });
  }

  public async openExpressionEditor(
    parameter: ParameterDefinition,
    parameterDefinitions: ParameterDefinition[]
  ): Promise<void> {
    const initialValue = parameter.DataArgument2;

    const ovm = await this.abcCore.objectSpace.getObjectType(parameter.DataArgument).toPromise();

    const expressionEditorResultAction = async (expressionResult: ExpressionEditorResult) => {
      if (expressionResult) {
        parameter.DataArgument2 = expressionResult?.oPath;

        this.onNewParameterDefinitions.emit(expressionResult.parameterDefinitions);
      }

      this.removeParameterDefinitionsQueueElement(this.editorService.editorIndex);
    };

    const settings: ExpressionEditorSettings = new ExpressionEditorSettings();
    settings.objectTypeInfoViewModels = [ovm];
    settings.oPath = initialValue;
    settings.parameterDefinitions = parameterDefinitions.filter((_) => _.IsUserCreated && _.Name !== parameter.Name);
    settings.validationType = ValidationType.Column;
    settings.editorInstanceSettings = new EditorInstanceSettings({
      editorId: '',
      initialValue: '',
      expressionsEditorResultAction: expressionEditorResultAction,
    });

    if (initialValue) {
      settings.editorInstanceSettings.initialValue = initialValue;
    }

    this.expressionsEditorService.openExpressionEditor(settings);
  }

  public async openBce(): Promise<void> {
    this.bceService.bceOpen();
  }
}
